import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  Paper,
  TextField,
} from '@mui/material';
import { Schedule as ScheduleIcon, Send as SendIcon } from '@mui/icons-material';
import { format } from 'date-fns';

interface ScheduleSelectorProps {
  scheduledFor?: Date;
  onScheduleChange: (date: Date | undefined) => void;
  disabled?: boolean;
}

/**
 * ScheduleSelector - Component for scheduling broadcast delivery
 * Supports immediate send or scheduled future delivery
 */
const ScheduleSelector: React.FC<ScheduleSelectorProps> = ({
  scheduledFor,
  onScheduleChange,
  disabled = false,
}) => {
  const [mode, setMode] = React.useState<'immediate' | 'scheduled'>(
    scheduledFor ? 'scheduled' : 'immediate'
  );

  const handleModeChange = (newMode: 'immediate' | 'scheduled') => {
    setMode(newMode);
    if (newMode === 'immediate') {
      onScheduleChange(undefined);
    } else {
      // Set default to 1 hour from now
      const defaultTime = new Date();
      defaultTime.setHours(defaultTime.getHours() + 1);
      onScheduleChange(defaultTime);
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Validate that date is in the future
      if (date <= new Date()) {
        // If date is in the past, set to 1 hour from now
        const futureDate = new Date();
        futureDate.setHours(futureDate.getHours() + 1);
        onScheduleChange(futureDate);
      } else {
        onScheduleChange(date);
      }
    }
  };

  const minDateTime = new Date();
  minDateTime.setMinutes(minDateTime.getMinutes() + 5); // Minimum 5 minutes from now

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Delivery Schedule
      </Typography>

      <FormControl component="fieldset" fullWidth disabled={disabled}>
        <RadioGroup
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as any)}
        >
          <FormControlLabel
            value="immediate"
            control={<Radio />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <SendIcon fontSize="small" />
                <Typography>Send Immediately</Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="scheduled"
            control={<Radio />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <ScheduleIcon fontSize="small" />
                <Typography>Schedule for Later</Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      {mode === 'immediate' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          The broadcast will be sent immediately after you click "Send Broadcast".
        </Alert>
      )}

      {mode === 'scheduled' && (
        <Paper sx={{ p: 3, mt: 2 }} variant="outlined">
          <TextField
            fullWidth
            label="Scheduled Date & Time"
            type="datetime-local"
            value={scheduledFor ? format(scheduledFor, "yyyy-MM-dd'T'HH:mm") : ''}
            onChange={(e) => handleDateChange(e.target.value ? new Date(e.target.value) : null)}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: format(minDateTime, "yyyy-MM-dd'T'HH:mm"),
            }}
            helperText={scheduledFor
              ? `Broadcast will be sent on ${format(scheduledFor, 'PPpp')}`
              : 'Select a future date and time'}
          />

          {scheduledFor && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Scheduled broadcasts require a background job or cron task to be set up.
                The broadcast will be marked as "scheduled" but manual intervention may be needed to send it
                at the specified time.
              </Typography>
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ScheduleSelector;
